import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, Trash2, Loader2, Calendar as CalendarIcon, Users, Eye, EyeOff,
  ChevronDown, ChevronRight, X, Flame, CheckCircle2,
} from "lucide-react";

const PRIORITY_STYLE = {
  low: { bar: "bg-gray-300", chip: "bg-gray-100 text-gray-600" },
  medium: { bar: "bg-njas-sage", chip: "bg-njas-sage-tint text-njas-sage-hover" },
  high: { bar: "bg-red-400", chip: "bg-red-50 text-red-700" },
};

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtDay(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff < 7) return d.toLocaleDateString("en-AU", { weekday: "long" });
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

export default function Todos() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [busy, setBusy] = useState(null);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get("/todos"),
        api.get("/clients").catch(() => ({ data: [] })),
      ]);
      setItems(t.data || []);
      setClients(c.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { mine, shared } = useMemo(() => {
    const m = [], s = [];
    (items || []).forEach((t) => {
      if (t.user_id === user?.id) m.push(t);
      else s.push(t); // admin viewing shared
    });
    return { mine: m, shared: s };
  }, [items, user?.id]);

  const pending = mine.filter((t) => !t.completed);
  const done = mine.filter((t) => t.completed);
  const overdue = pending.filter((t) => t.due_date && t.due_date < todayIso()).length;

  const toggle = async (t) => {
    setBusy(t.id);
    try {
      await api.patch(`/todos/${t.id}`, { completed: !t.completed });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const del = async (t) => {
    if (!window.confirm("Delete this to-do?")) return;
    setBusy(t.id);
    try {
      await api.delete(`/todos/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="fade-in max-w-3xl" data-testid="todos-page">
      <div className="mb-6">
        <div className="eyebrow text-njas-sage-hover text-[10px]">Accountability</div>
        <h2 className="font-serif text-3xl text-njas-navy">To-do list</h2>
        <p className="text-sm text-njas-muted mt-1">
          {loading ? "Loading…"
            : pending.length === 0 ? "Nothing on your list. Add one below."
            : `${pending.length} open${overdue ? ` · ${overdue} overdue` : ""}${done.length ? ` · ${done.length} done` : ""}`}
        </p>
      </div>

      <QuickAdd onAdded={load} clients={clients} />

      {/* Pending */}
      <div className="mt-6 space-y-2">
        {loading && <div className="text-sm text-njas-muted">Loading…</div>}
        {!loading && pending.length === 0 && (
          <div className="text-sm text-njas-muted italic py-6 text-center border border-dashed border-[var(--njas-border)] rounded-xl">
            Nothing to do. Add your first one above.
          </div>
        )}
        {pending.map((t) => (
          <TodoRow key={t.id} t={t} busy={busy} onToggle={toggle} onDelete={del} onEdit={() => setEditing(t)} />
        ))}
      </div>

      {/* Completed */}
      {done.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            data-testid="todos-show-completed"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-njas-muted hover:text-njas-navy"
          >
            {showCompleted ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            Completed ({done.length})
          </button>
          {showCompleted && (
            <div className="mt-2 space-y-2">
              {done.map((t) => (
                <TodoRow key={t.id} t={t} busy={busy} onToggle={toggle} onDelete={del} onEdit={() => setEditing(t)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Admin-shared todos */}
      {user?.role === "admin" && shared.length > 0 && (
        <div className="mt-8 pt-6 border-t border-[var(--njas-border)]">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-njas-muted" />
            <h3 className="font-serif text-xl text-njas-navy">Shared with admins</h3>
          </div>
          <div className="space-y-2">
            {shared.map((t) => (
              <TodoRow key={t.id} t={t} busy={busy} onToggle={toggle} onDelete={del} onEdit={() => setEditing(t)} readOnly />
            ))}
          </div>
        </div>
      )}

      {editing && (
        <EditTodoModal
          t={editing}
          clients={clients}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TodoRow({ t, busy, onToggle, onDelete, onEdit, readOnly }) {
  const overdue = !t.completed && t.due_date && t.due_date < todayIso();
  const p = PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.medium;
  return (
    <div
      data-testid={`todo-row-${t.id}`}
      className={`group flex items-start gap-3 rounded-xl border p-3 transition-colors ${
        t.completed ? "bg-njas-bg-alt/40 border-[var(--njas-border)]"
        : overdue ? "bg-red-50/30 border-red-200"
        : "bg-white border-[var(--njas-border)] hover:border-njas-sage"
      }`}
    >
      <button
        onClick={() => onToggle(t)}
        disabled={busy === t.id}
        data-testid={`todo-check-${t.id}`}
        className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center shrink-0 border-2 transition-colors ${
          t.completed ? "bg-emerald-500 border-emerald-500 text-white" : "border-njas-muted/40 hover:border-njas-sage"
        }`}
        aria-label={t.completed ? "Mark as pending" : "Mark as complete"}
      >
        {busy === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : t.completed && <CheckCircle2 className="w-3.5 h-3.5" />}
      </button>

      <div className="flex-1 min-w-0 cursor-pointer" onClick={!readOnly ? onEdit : undefined}>
        <div className={`text-sm font-medium ${t.completed ? "text-njas-muted line-through" : "text-njas-navy"}`}>{t.title}</div>
        <div className="mt-1 flex items-center flex-wrap gap-1.5 text-xs">
          {t.priority && t.priority !== "medium" && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${p.chip}`}>
              {t.priority === "high" && <Flame className="w-2.5 h-2.5" />}
              {t.priority}
            </span>
          )}
          {t.due_date && (
            <span className={`inline-flex items-center gap-1 ${overdue ? "text-red-600 font-medium" : "text-njas-muted"}`}>
              <CalendarIcon className="w-3 h-3" />
              {fmtDay(t.due_date)}
            </span>
          )}
          {t.client_name && (
            <span className="inline-flex items-center gap-1 text-njas-muted">
              · {t.client_name}
            </span>
          )}
          {t.shared_with_admins && (
            <span className="inline-flex items-center gap-1 text-njas-sage-hover">
              <Eye className="w-3 h-3" /> shared
            </span>
          )}
          {readOnly && t.user_name && <span className="text-njas-muted">· by {t.user_name}</span>}
        </div>
        {t.notes && <div className="mt-1 text-xs text-njas-muted whitespace-pre-wrap line-clamp-2">{t.notes}</div>}
      </div>

      {!readOnly && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(t); }}
          data-testid={`todo-delete-${t.id}`}
          disabled={busy === t.id}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-njas-muted hover:text-red-600 p-1"
          aria-label="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function QuickAdd({ onAdded, clients }) {
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("medium");
  const [shared, setShared] = useState(false);
  const [clientId, setClientId] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post("/todos", {
        title: title.trim(),
        due_date: dueDate || null,
        priority,
        shared_with_admins: shared,
        client_id: clientId || null,
      });
      setTitle(""); setDueDate(""); setPriority("medium"); setShared(false); setClientId(""); setShowMore(false);
      onAdded();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-xl bg-white border border-[var(--njas-border)] p-3">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded-md border-2 border-njas-muted/30 shrink-0" />
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add a to-do (press Enter to save)…"
          className="flex-1 bg-transparent outline-none text-sm text-njas-navy placeholder:text-njas-muted"
          data-testid="todo-quick-add-input"
        />
        <button
          type="button"
          onClick={() => setShowMore(!showMore)}
          className="text-xs text-njas-muted hover:text-njas-navy px-2 py-1"
          data-testid="todo-quick-add-more"
        >
          {showMore ? "Less" : "More"}
        </button>
        <button
          type="submit"
          disabled={busy || !title.trim()}
          data-testid="todo-quick-add-submit"
          className="inline-flex items-center gap-1 rounded-full bg-njas-navy text-white text-xs px-3 py-1.5 hover:bg-njas-navy-hover disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add
        </button>
      </div>

      {showMore && (
        <div className="mt-3 pt-3 border-t border-[var(--njas-border)] grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Due date</span>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} data-testid="todo-quick-add-date" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputCls} data-testid="todo-quick-add-priority">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Link to client (optional — kept private)</span>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls} data-testid="todo-quick-add-client">
              <option value="">No client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </label>
          <label className="flex items-start gap-2 sm:col-span-2 text-sm cursor-pointer">
            <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} className="mt-0.5" data-testid="todo-quick-add-shared" />
            <span>
              <span className="text-njas-navy font-medium">Share with admins</span>
              <span className="block text-xs text-njas-muted">Admins will see this to-do in their shared list. Off by default.</span>
            </span>
          </label>
        </div>
      )}
    </form>
  );
}

function EditTodoModal({ t, clients, onClose, onSaved }) {
  const { user } = useAuth();
  const isOwner = t.user_id === user?.id;
  const [title, setTitle] = useState(t.title);
  const [notes, setNotes] = useState(t.notes || "");
  const [dueDate, setDueDate] = useState(t.due_date || "");
  const [priority, setPriority] = useState(t.priority || "medium");
  const [shared, setShared] = useState(!!t.shared_with_admins);
  const [clientId, setClientId] = useState(t.client_id || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const payload = { title, notes, due_date: dueDate || null, priority, client_id: clientId || "" };
      if (isOwner) payload.shared_with_admins = shared;
      await api.patch(`/todos/${t.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="todo-edit-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow text-njas-sage-hover text-[10px]">Edit</div>
            <h3 className="font-serif text-2xl text-njas-navy">Edit to-do</h3>
          </div>
          <button onClick={onClose} className="p-1 text-njas-muted hover:text-njas-navy"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="todo-edit-title" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Notes</span>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} data-testid="todo-edit-notes" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Due date</span>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} data-testid="todo-edit-date" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Priority</span>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputCls} data-testid="todo-edit-priority">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-njas-muted block mb-1">Linked client (optional)</span>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls} data-testid="todo-edit-client">
              <option value="">No client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </label>
          {isOwner && (
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} className="mt-0.5" data-testid="todo-edit-shared" />
              <span>
                <span className="text-njas-navy font-medium inline-flex items-center gap-1">
                  {shared ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  Share with admins
                </span>
                <span className="block text-xs text-njas-muted">Admins can see this to-do in their shared list.</span>
              </span>
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-[var(--njas-border)]">
          <button onClick={onClose} className="rounded-full px-5 py-1.5 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="todo-edit-save" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-1.5 text-sm hover:bg-njas-navy-hover disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
